/**
 * 侧边栏导航服务 - v2版
 * @description 提供侧边栏导航、标题管理、面包屑等功能的统一服务
 * @author Mech3D Team
 * @version v2
 */

import type {
  BreadcrumbItem,
  SidebarNavigationItem,
} from '@/config/navigation/sidebarNavigationConfig'
import {
  getFilteredNavigation,
  NAVIGATION_GROUPS,
} from '@/config/navigation/sidebarNavigationConfig'

/**
 * 导航分组接口 - 用于侧边栏显示
 */
export interface NavigationGroup {
  key: string
  title: string
  icon: string
  children: SidebarNavigationItem[]
}

/**
 * 权限检查器接口
 */
export type PermissionChecker = (permission: string, userPermissions: string[]) => boolean

/**
 * 侧边栏导航服务类
 * @description 提供统一的导航数据API，替代原有的复杂导航服务
 */
export class SidebarNavigationService {
  private static instance: SidebarNavigationService
  private cache = new Map<string, any>()
  private debug: boolean = import.meta.env.DEV

  private constructor() { }

  /**
   * 获取服务实例（单例模式）
   */
  static getInstance(): SidebarNavigationService {
    if (!SidebarNavigationService.instance) {
      SidebarNavigationService.instance = new SidebarNavigationService()
    }
    return SidebarNavigationService.instance
  }

  /**
   * 获取页面标题 - 统一接口
   * @param path 路由路径
   * @returns 页面标题
   */
  getTitle(path: string): string {
    const cacheKey = `title-${path}`

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }

    const navigationItem = this.getNavigationItem(path)
    const title = navigationItem?.title || ''

    // if (this.debug) {
    // }

    this.cache.set(cacheKey, title)
    return title
  }

  /**
   * 获取面包屑 - 统一接口
   * @param path 路由路径
   * @returns 面包屑数组
   */
  getBreadcrumb(path: string): BreadcrumbItem[] {
    const cacheKey = `breadcrumb-${path}`

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }

    const navigationItem = this.getNavigationItem(path)
    const breadcrumb = navigationItem?.breadcrumb || []

    // if (this.debug) {
    // }

    this.cache.set(cacheKey, breadcrumb)
    return breadcrumb
  }

  /**
   * 获取导航项 - 核心查找方法
   * @param path 路由路径
   * @returns 导航项或null
   */
  getNavigationItem(path: string): SidebarNavigationItem | null {
    const filteredNavigation = getFilteredNavigation()
    const item = filteredNavigation.find(nav => nav.path === path)

    // if (this.debug && item) {
    // }

    return item || null
  }

  /**
   * 获取侧边栏分组数据 - 权限过滤版
   * @param userPermissions 用户权限数组
   * @param permissionChecker 权限检查函数
   * @returns 侧边栏分组数据
   */
  getSidebarItems(
    userPermissions: string[] = [],
    permissionChecker?: PermissionChecker,
  ): NavigationGroup[] {
    const cacheKey = `sidebar-${userPermissions.join(',')}`

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }

    const filteredNavigation = getFilteredNavigation()
    const checker = permissionChecker || this.defaultPermissionChecker

    // 权限过滤
    const accessibleItems = filteredNavigation.filter((item) => {
      if (item.permissions.length === 0) {
        return true // 无权限要求的项目直接显示
      }

      return item.permissions.some(permission =>
        checker(permission, userPermissions),
      )
    })

    // 按分类分组
    const groups: NavigationGroup[] = []
    const categoryMap = new Map<string, SidebarNavigationItem[]>()

    // 收集各分类的导航项
    accessibleItems.forEach((item) => {
      const category = item.meta.category
      if (!categoryMap.has(category)) {
        categoryMap.set(category, [])
      }
      categoryMap.get(category)!.push(item)
    })

    // 构建分组数据
    Object.values(NAVIGATION_GROUPS).forEach((groupConfig) => {
      const items = categoryMap.get(groupConfig.key) || []
      if (items.length > 0) {
        // 按order排序
        items.sort((a, b) => a.meta.order - b.meta.order)

        groups.push({
          key: groupConfig.key,
          title: groupConfig.title,
          icon: groupConfig.icon,
          children: items,
        })
      }
    })

    // 按分组顺序排序
    groups.sort((a, b) => {
      const aOrder = NAVIGATION_GROUPS[a.key as keyof typeof NAVIGATION_GROUPS]?.order || 999
      const bOrder = NAVIGATION_GROUPS[b.key as keyof typeof NAVIGATION_GROUPS]?.order || 999
      return aOrder - bOrder
    })

    // if (this.debug) {

    this.cache.set(cacheKey, groups)
    return groups
  }

  /**
   * 获取所有可访问的路径 - 用于路由配置
   * @param userPermissions 用户权限数组
   * @param permissionChecker 权限检查函数
   * @returns 可访问的路径数组
   */
  getAccessiblePaths(
    userPermissions: string[] = [],
    permissionChecker?: PermissionChecker,
  ): string[] {
    const filteredNavigation = getFilteredNavigation()
    const checker = permissionChecker || this.defaultPermissionChecker

    return filteredNavigation
      .filter((item) => {
        if (item.permissions.length === 0)
          return true
        return item.permissions.some(permission =>
          checker(permission, userPermissions),
        )
      })
      .map(item => item.path)
  }

  /**
   * 检查路径权限
   * @param path 路由路径
   * @param userPermissions 用户权限数组
   * @param permissionChecker 权限检查函数
   * @returns 是否有权限访问
   */
  hasPermission(
    path: string,
    userPermissions: string[] = [],
    permissionChecker?: PermissionChecker,
  ): boolean {
    const navigationItem = this.getNavigationItem(path)
    if (!navigationItem)
      return false

    if (navigationItem.permissions.length === 0)
      return true

    const checker = permissionChecker || this.defaultPermissionChecker
    return navigationItem.permissions.some(permission =>
      checker(permission, userPermissions),
    )
  }

  /**
   * 获取模块类型
   * @param path 路由路径
   * @returns 模块类型
   */
  getModuleType(path: string): 'user' | 'service' | 'admin' | 'unknown' {
    if (path.startsWith('/user'))
      return 'user'
    if (path.startsWith('/service'))
      return 'service'
    if (path.startsWith('/admin'))
      return 'admin'
    return 'unknown'
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.cache.clear()
    // if (this.debug) {
    // }
  }

  /**
   * 获取缓存统计
   */
  getCacheStats(): { size: number, keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    }
  }

  /**
   * 默认权限检查器 - 简单匹配
   * @private
   */
  private defaultPermissionChecker: PermissionChecker = (permission, userPermissions) => {
    return userPermissions.includes(permission)
  }
}

/**
 * 导出服务实例
 */
export const sidebarNavigationService = SidebarNavigationService.getInstance()

/**
 * 导出便捷函数 - 向后兼容
 */
export function getTitle(path: string): string {
  return sidebarNavigationService.getTitle(path)
}

export function getBreadcrumb(path: string): BreadcrumbItem[] {
  return sidebarNavigationService.getBreadcrumb(path)
}

export function getSidebarItems(userPermissions: string[] = []): NavigationGroup[] {
  return sidebarNavigationService.getSidebarItems(userPermissions)
}

export function getNavigationItem(path: string): SidebarNavigationItem | null {
  return sidebarNavigationService.getNavigationItem(path)
}

export function hasPermission(
  path: string,
  userPermissions: string[] = [],
): boolean {
  return sidebarNavigationService.hasPermission(path, userPermissions)
}

/**
 * Vue Router集成函数
 */
export function generatePageTitle(route: { path: string }): string {
  return sidebarNavigationService.getTitle(route.path)
}

export function generateBreadcrumb(route: { path: string }): BreadcrumbItem[] {
  return sidebarNavigationService.getBreadcrumb(route.path)
}
